import { Shield, Award } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { trackBadgeClick, trackPDFDownload } from '../utils/analytics';

/**
 * CertificationBadges Component
 * 
 * Displays trust signals and certifications.
 * Increases credibility and conversion rates.
 * 
 * Badges:
 * - ISO 9001 Certification (with PDF link)
 * - Authorized Dynalab Integrator
 * 
 * @param {Object} props
 * @param {Array} props.badges - Which badges to show: ['iso', 'dynalab'] (default: both)
 * @param {string} props.layout - 'horizontal' or 'vertical' (default: 'horizontal')
 * @param {string} props.className - Additional CSS classes
 * 
 * @example
 * // Show both badges
 * <CertificationBadges />
 * 
 * @example
 * // Show only ISO badge (for CNC page)
 * <CertificationBadges badges={['iso']} />
 */
const CertificationBadges = ({ 
  badges = ['iso', 'dynalab'],
  layout = 'horizontal', 
  className = '' 
}) => {
  const { language } = useLanguage();
  
  const translations = {
    en: {
      iso: {
        title: 'ISO 9001',
        subtitle: 'Certified Company'
      },
      dynalab: {
        title: 'Authorized Integrator',
        subtitle: 'Dynalab'
      }
    },
    es: {
      iso: {
        title: 'ISO 9001',
        subtitle: 'Empresa Certificada'
      },
      dynalab: {
        title: 'Integrador Autorizado',
        subtitle: 'Dynalab'
      }
    }
  };

  const t = translations[language];
  
  const containerClass = layout === 'vertical' 
    ? 'flex flex-col gap-4'
    : 'flex flex-row flex-wrap gap-4 items-center justify-center';

  const showISO = badges.includes('iso');
  const showDynalab = badges.includes('dynalab');

  return (
    <div className={`${containerClass} ${className}`}>
      {/* ISO 9001 Badge */}
      {showISO && (
        <a 
          href="/documents/ISO-9001-Certificate.pdf" 
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackBadgeClick('iso', window.location.pathname);
            trackPDFDownload('ISO-9001-Certificate', window.location.pathname);
          }}
          className="group flex items-center gap-4 bg-gradient-to-r from-gray-900/80 to-gray-800/80 border-2 border-[#B91C1C]/40 hover:border-[#B91C1C] rounded-xl px-8 py-4 transition-all duration-300 hover:shadow-xl hover:shadow-[#B91C1C]/30 hover:scale-105 min-w-[280px]"
        >
          <div className="flex-shrink-0 w-14 h-14 rounded-full bg-[#B91C1C]/20 flex items-center justify-center group-hover:bg-[#B91C1C]/30 transition-colors">
            <Shield className="w-8 h-8 text-[#B91C1C]" />
          </div>
          <div className="flex flex-col">
            <span className="text-white font-bold text-lg">{t.iso.title}</span>
            <span className="text-gray-300 text-base font-medium">{t.iso.subtitle}</span>
          </div>
        </a>
      )}

      {/* Dynalab Badge */}
      {showDynalab && (
        <div 
          onClick={() => trackBadgeClick('dynalab', window.location.pathname)}
          className="flex items-center gap-4 bg-gradient-to-r from-gray-900/80 to-gray-800/80 border-2 border-blue-500/40 rounded-xl px-8 py-4 hover:border-blue-400 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/20 hover:scale-105 min-w-[280px] cursor-pointer">
          <div className="flex-shrink-0 w-20 h-14 bg-white/15 rounded-lg flex items-center justify-center p-2">
            <img 
              src="/images/dynalab-logo.png" 
              alt="Dynalab Test Systems" 
              className="w-full h-full object-contain"
            />
          </div>
          <div className="flex flex-col">
            <span className="text-white font-bold text-lg">{t.dynalab.title}</span>
            <span className="text-blue-300 text-base font-medium">{t.dynalab.subtitle}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificationBadges;
